/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useState, useContext, useEffect, useMemo } from 'react';
import styled, { ThemeContext } from 'styled-components';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';

import {
  CpdsSweepstakesFormsToolkitsInputFieldsWrapper,
  CpdsSweepstakesFormsToolkitsLabelWrapper,
  CpdsSweepstakesFormsToolkitsLabel,
  CpdsSweepstakesFormsToolkitsTextFieldWrapper,
  CpdsSweepstakesFormsToolkitsTextField,
  CpdsSweepstakesFormsToolkitsHelperTextWrapper,
  CpdsSweepstakesFormsToolkitsHelperLabel
} from '../styles/cpds-sweepstakes-forms-toolkits-inputs-style';
import { CpdsSweepstakesFormsToolkitsFieldsProps, BorderType } from '../typings';

import { Cpds } from '@Cpds.Ui';
import { themeToken } from '../../utils/cpds-utils';

const Fields: React.FC<CpdsSweepstakesFormsToolkitsFieldsProps> = ({
  inputId = "",
  inputValue = "",
  domRef,
  exClasses = "",
  styleOverrides = {},
  onChange = () => { },
  getValue = () => { },
  inputWidth,
  isDisabled,
  LabelCopy,
  LabelIcon,
  LabelIconSize = { width: '10', height: '10' },
  PlaceholderCopy = '',
  HelperCopy = false,
  HelperIcon = false,
  HelperIconSize = false,
  isError = false,
  ErrorIcon = 'information',
  ErrorIconSize = { width: '12', height: '12' },
  ErrorMessage = null,
  isOverRideTypography = false,
  borderType = BorderType.RoundPill,
  setChange = '',
  ...rest
}) => {
  const { themeColorMode } = rest;
  const [isFocused, setIsFocused] = useState(false);
  const themeContext: any = useContext(ThemeContext);
  const cpds = themeContext?.styles?.cpds;
  const token = useMemo(() => themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode).token, [cpds, rest]);

  const [inputData, setInputData] = useState(inputValue);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  useEffect(() => {
    getValue(inputData);
  }, [inputData, getValue]);

  const onInputChange = useCallback(
    (e) => {
      onChange(e);
      setInputData(e.target.value);
      getValue(e);
    },
    [onChange, getValue]
  );

  const [enableEffect, setEnableEffect] = useState(setChange);

  useEffect(() => {
    setInputData(setChange);
  }, [setChange]);

  const getLabelContent = () => {
    if (typeof LabelCopy === 'string') {
      const sanitizedHTML = DOMPurify.sanitize(LabelCopy);
      return parse(sanitizedHTML);
    } else if (React.isValidElement(LabelCopy) && LabelCopy.props.children) {
      const children = React.Children.map(LabelCopy.props.children, (child) => {
        if (typeof child === 'string') {
          return parse(DOMPurify.sanitize(child));
        }
        return child;
      });
      return <>{children}</>;
    }
    return '';
  };

  return (
    <CpdsSweepstakesFormsToolkitsInputFieldsWrapper
      className={`cpds-input-fields ${exClasses} ${isError ? 'invalid' : ''}`}
      ref={domRef}
      styleOverrides={styleOverrides}
      inputWidth={inputWidth}
    >
      {LabelCopy && (
        <CpdsSweepstakesFormsToolkitsLabelWrapper>
          <CpdsSweepstakesFormsToolkitsLabel
            className="inputbox-top-label cpds-body-roman-medium-08"
            isDisabled={isDisabled}
            themeColorMode={themeColorMode}
            isOverRideTypography={isOverRideTypography}
          >
            {getLabelContent()}
          </CpdsSweepstakesFormsToolkitsLabel>

          {LabelIcon && (
            <Cpds.Icon
              id="ico-1"
              name={LabelIcon}
              customSize={LabelIconSize}
              color={
                isDisabled
                  ? token?.input_fields_label_disable_icon_color || '#31374F'
                  : token?.input_fields_label_icon_color || '#31374F'
              }
            />
          )}
        </CpdsSweepstakesFormsToolkitsLabelWrapper>
      )}
      <CpdsSweepstakesFormsToolkitsTextFieldWrapper
        className="text-field-wrapper"
        isDisabled={isDisabled}
        themeColorMode={themeColorMode}
        borderType={borderType}
      >

        <CpdsSweepstakesFormsToolkitsTextField
          id={inputId}
          value={inputData}
          type="text"
          placeholder={PlaceholderCopy}
          aria-label={PlaceholderCopy}
          onFocus={handleFocus}
          onBlur={handleBlur}
          isDisabled={isDisabled}
          disabled={isDisabled}
          onChange={onInputChange}
          className="cpds-meta-italics-book-09"
        />
      </CpdsSweepstakesFormsToolkitsTextFieldWrapper>
      {(isError && ErrorMessage && ErrorMessage !== '') && (
        <CpdsSweepstakesFormsToolkitsHelperTextWrapper
          className="sweepstake-error-wrapper"
        >
          {(ErrorIcon) && (
            <Cpds.Icon
              id="ico-3"
              color={
                isError
                  ? token?.input_fields_error_label_color
                  : isDisabled
                    ? token?.input_fields_helper_icon_disable_color
                    : token?.input_fields_helper_label_color
              }
              name={isError ? ErrorIcon : ""}
              customSize={ErrorIconSize}
            />
          )}
          <CpdsSweepstakesFormsToolkitsHelperLabel
            isError={isError}
            isDisabled={isDisabled}
            themeColorMode={themeColorMode}
            isOverRideTypography={isOverRideTypography}
            className="cpds-meta-roman-book-10"
          >
            {isError ? ErrorMessage : ""}
          </CpdsSweepstakesFormsToolkitsHelperLabel>
        </CpdsSweepstakesFormsToolkitsHelperTextWrapper>
      )}
    </CpdsSweepstakesFormsToolkitsInputFieldsWrapper>
  );
};

export default Fields;
